/**
 * PostHog Analytics Configuration
 * 
 * Tracks user behavior across the application:
 * - Portfolio management (create, update, delete)
 * - Position tracking (create, close, detail view)
 * - Import operations (start, complete)
 * - Feature usage (AI review, stoploss updates)
 * 
 * Note: Analytics enabled only on production domain (tradingjournal.co.in)
 */

import posthog from 'posthog-js';

const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// Detect production by hostname
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  // Only enable on production domain
  return hostname === 'tradingjournal.co.in' || hostname === 'www.tradingjournal.co.in';
};

const IS_PROD = isProduction();

// Initialize PostHog (only in production)
export const initPostHog = () => {
  if (!IS_PROD) {
    console.log(`[Analytics] Disabled - hostname: ${typeof window !== 'undefined' ? window.location.hostname : 'server'}`);
    return posthog;
  }
  
  if (typeof window !== 'undefined' && !posthog.__loaded && POSTHOG_API_KEY) {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      person_profiles: 'identified_only',
      persistence: 'localStorage',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-mask]',
      },
    });
    console.log('[Analytics] PostHog initialized');
  }
  return posthog;
};

// Event name constants for consistency
export const ANALYTICS_EVENTS = {
  // User events
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  
  // Portfolio events
  PORTFOLIO_CREATED: 'portfolio_created',
  PORTFOLIO_UPDATED: 'portfolio_updated',
  PORTFOLIO_DELETED: 'portfolio_deleted',
  PORTFOLIO_VIEWED: 'portfolio_viewed',
  
  // Position events
  POSITION_CREATED: 'position_created',
  POSITION_CLOSED: 'position_closed',
  POSITION_DETAIL_CLICKED: 'position_detail_clicked',
  POSITION_UPDATED: 'position_updated',
  
  // Import events
  IMPORT_MODAL_OPENED: 'import_modal_opened',
  IMPORT_STARTED: 'import_started',
  IMPORT_COMPLETED: 'import_completed',
  IMPORT_FAILED: 'import_failed',
  IMPORT_CANCELLED: 'import_cancelled',
  
  // Feature events
  STOPLOSS_UPDATED: 'stoploss_updated',
  AI_REVIEW_CLICKED: 'ai_review_clicked',
  AI_REVIEW_COMPLETED: 'ai_review_completed',
  
  // Navigation events
  PAGE_VIEW: '$pageview',
};

// Analytics helper functions
export const analytics = {
  /**
   * Identify a user after login
   */
  identify: (userId: string, traits?: Record<string, any>) => {
    if (!IS_PROD) return;
    posthog.identify(userId, {
      ...traits,
      identified_at: new Date().toISOString(),
    });
  },

  /**
   * Reset user identity on logout
   */
  reset: () => {
    if (!IS_PROD) return;
    posthog.reset();
  },

  /**
   * Track a custom event
   */
  track: (eventName: string, properties?: Record<string, any>) => {
    if (!IS_PROD) {
      console.log(`[Analytics] Event: ${eventName}`, properties);
      return;
    }
    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  },

  // ============================================
  // User Events
  // ============================================
  
  trackLogin: (userId: string, email: string, name?: string) => {
    analytics.identify(userId, { email, name, provider: 'google' });
    analytics.track(ANALYTICS_EVENTS.USER_LOGIN, {
      provider: 'google',
      email,
    });
  },

  trackLogout: () => {
    analytics.track(ANALYTICS_EVENTS.USER_LOGOUT);
    analytics.reset();
  },

  // ============================================
  // Portfolio Events
  // ============================================
  
  trackPortfolioCreated: (portfolioId: string, name: string, totalSize?: number) => {
    analytics.track(ANALYTICS_EVENTS.PORTFOLIO_CREATED, {
      portfolio_id: portfolioId,
      portfolio_name: name,
      total_size: totalSize,
    });
  },

  trackPortfolioUpdated: (portfolioId: string, changes: Record<string, any>) => {
    analytics.track(ANALYTICS_EVENTS.PORTFOLIO_UPDATED, {
      portfolio_id: portfolioId,
      changes: Object.keys(changes),
    });
  },

  trackPortfolioDeleted: (portfolioId: string, name: string) => {
    analytics.track(ANALYTICS_EVENTS.PORTFOLIO_DELETED, {
      portfolio_id: portfolioId,
      portfolio_name: name,
    });
  },

  trackPortfolioViewed: (portfolioId: string, positionCount: number) => {
    analytics.track(ANALYTICS_EVENTS.PORTFOLIO_VIEWED, {
      portfolio_id: portfolioId,
      position_count: positionCount,
    });
  },

  // ============================================
  // Position Events
  // ============================================
  
  trackPositionCreated: (positionId: string, ticker: string, portfolioId: string) => {
    analytics.track(ANALYTICS_EVENTS.POSITION_CREATED, {
      position_id: positionId,
      ticker,
      portfolio_id: portfolioId,
    });
  },

  trackPositionClosed: (positionId: string, ticker: string, pnl: number, roi: number) => {
    analytics.track(ANALYTICS_EVENTS.POSITION_CLOSED, {
      position_id: positionId,
      ticker,
      realized_pnl: pnl,
      roi_percent: roi,
      outcome: pnl > 0 ? 'win' : 'loss',
    });
  },

  trackPositionDetailClicked: (positionId: string, ticker: string, source: string) => {
    analytics.track(ANALYTICS_EVENTS.POSITION_DETAIL_CLICKED, {
      position_id: positionId,
      ticker,
      source, // e.g., 'portfolio_list', 'journal', 'search'
    });
  },

  // ============================================
  // Import Events
  // ============================================
  
  trackImportModalOpened: (portfolioId: string) => {
    analytics.track(ANALYTICS_EVENTS.IMPORT_MODAL_OPENED, {
      portfolio_id: portfolioId,
    });
  },

  trackImportStarted: (broker: string, portfolioId: string) => {
    analytics.track(ANALYTICS_EVENTS.IMPORT_STARTED, {
      broker,
      portfolio_id: portfolioId,
    });
  },

  trackImportCompleted: (broker: string, portfolioId: string, recordCount: number, newPositions: number) => {
    analytics.track(ANALYTICS_EVENTS.IMPORT_COMPLETED, {
      broker,
      portfolio_id: portfolioId,
      record_count: recordCount,
      new_positions: newPositions,
    });
  },

  trackImportFailed: (broker: string, portfolioId: string, error: string) => {
    analytics.track(ANALYTICS_EVENTS.IMPORT_FAILED, {
      broker,
      portfolio_id: portfolioId,
      error,
    });
  },

  trackImportCancelled: (portfolioId: string, stage: string) => {
    analytics.track(ANALYTICS_EVENTS.IMPORT_CANCELLED, {
      portfolio_id: portfolioId,
      stage, // 'broker_selection', 'file_upload', 'position_review'
    });
  },

  // ============================================
  // Feature Events
  // ============================================
  
  trackStoplossUpdated: (positionId: string, ticker: string, oldValue: number, newValue: number) => {
    analytics.track(ANALYTICS_EVENTS.STOPLOSS_UPDATED, {
      position_id: positionId,
      ticker,
      old_stoploss: oldValue,
      new_stoploss: newValue,
      change_percent: oldValue > 0 ? ((newValue - oldValue) / oldValue * 100).toFixed(2) : null,
    });
  },

  trackAIReviewClicked: (portfolioId: string, positionCount: number) => {
    analytics.track(ANALYTICS_EVENTS.AI_REVIEW_CLICKED, {
      portfolio_id: portfolioId,
      position_count: positionCount,
    });
  },

  trackAIReviewCompleted: (portfolioId: string, duration: number) => {
    analytics.track(ANALYTICS_EVENTS.AI_REVIEW_COMPLETED, {
      portfolio_id: portfolioId,
      duration_seconds: duration,
    });
  },
};

export default posthog;
